import role20MfrsSchema from '../../../../assets/forms/role-710000-mfrs.form.json';
import role20MpersSchema from '../../../../assets/forms/role-710000-mpers.form.json';
import { MPERS_ACCOUNTING_STANDARD } from './schema-picker.util';

/**
 * Shared business rules for Role 20 (Notes - Corporate Information).
 *
 * Role 20 is the first lifted sheet whose schema is **accounting-standard
 * dependent**: the same logical sheet ships as two sibling form JSONs
 * (role-710000-mfrs.form.json and role-710000-mpers.form.json) that differ
 * only in qname prefix (ssmt-mfrs:* vs ssmt-mpers:*). The component picks
 * between them at mount time based on the filing's accounting standard; the
 * pure validator has to make the same selection from
 * SheetValidationContext.accountingStandard.
 *
 * Both the editor's role20-710000-notes-corporate-information.component.ts
 * AND role20NotesCorporateInformationValidator import from this file so the
 * selection literal lives in exactly one place. If the backend ever renames
 * the accounting-standard enum (e.g. MPERS is renamed), only this file
 * changes — the parity-check harness will catch any consumer that drifted.
 */

/**
 * Literal value of
 * filingPrerequisites['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements']
 * that selects the MPERS schema. Any other value (including undefined,
 * '', or 'Malaysian Financial Reporting Standards') falls back to MFRS,
 * matching the component's `let schemaFile = 'role-710000-mfrs.form.json'`
 * default assignment.
 */
export const ROLE20_MPERS_ACCOUNTING_STANDARD = MPERS_ACCOUNTING_STANDARD;

/** Filename of the MFRS (default) variant schema. */
export const ROLE20_MFRS_SCHEMA_FILE = 'role-710000-mfrs.form.json';

/** Filename of the MPERS variant schema. */
export const ROLE20_MPERS_SCHEMA_FILE = 'role-710000-mpers.form.json';

/**
 * Narrow shape of a role 20 schema control used by both the component's
 * renderer and the pure validator. The schemas carry richer metadata (ui,
 * error, ContextPolicy, etc.) but the validator only needs qname, required,
 * and requiredIfCrossSheet — keeping this type tight makes the validator
 * resilient to unrelated schema additions.
 */
export interface Role20SchemaControl {
  qname: string;
  required?: boolean;
  validators?: Array<{ name?: string }>;
  requiredIfCrossSheet?: {
    sheet: string;
    field: string;
    equals: string;
  };
}

export interface Role20Schema {
  role: string;
  sheetId: string;
  label: string;
  controls: Role20SchemaControl[];
}

/**
 * Resolve the correct role 20 schema filename for the given accounting
 * standard. Kept as a separate helper so the component's async fetch() and
 * the validator's static JSON import both derive their target from the same
 * selection rule.
 */
export function pickRole20SchemaFile(accountingStandard: string | undefined | null): string {
  if (accountingStandard === ROLE20_MPERS_ACCOUNTING_STANDARD) {
    return ROLE20_MPERS_SCHEMA_FILE;
  }
  return ROLE20_MFRS_SCHEMA_FILE;
}

/**
 * Resolve the actual role 20 schema object for the given accounting
 * standard. Used by the pure validator which cannot fetch() at validate-
 * time (the listing/dashboard's cache-miss path runs synchronously) so
 * both variants are imported eagerly and selected from memory.
 */
export function pickRole20Schema(
  accountingStandard: string | undefined | null
): Role20Schema {
  if (accountingStandard === ROLE20_MPERS_ACCOUNTING_STANDARD) {
    return role20MpersSchema as unknown as Role20Schema;
  }
  return role20MfrsSchema as unknown as Role20Schema;
}
